/**
 * Validation rules used in validation.js
 */

const validationRules = (() => {
  let _matching = {};

  /**
   * Set rule-logic matching
   */
  const init = () => {
    _matching = {
      required: required,
      phone: phone,
      name: name,
      email: email,
      file: file,
      size: size,
      nip: nip,
      postalCode: postalCode,
      emailAgreement: emailAgreement,
      phoneAgreement: phoneAgreement,
      agreementEmail: agreementEmail,
      agreementPhone: agreementPhone,
      minLength: minLength,
      maxLength: maxLength,
    };
  };

  /**
   * Return matching rule
   * @param rule
   */
  const returnValidationRule = (rule) => {
    rule = rule.trim();
    return _matching[rule] ?? (() => ({ error: false }));
  };

  /**
   * required field validation test. Sometimes it can be a boolean value
   *
   * @param {String|Boolean|Array} value
   * @returns {boolean}
   */
  const required = (value, type) => {
    if (type !== 'checkbox') value = value.replace(/\s+/g, '');

    if (!value) {
      return {
        error: true,
        msg: 'Pole wymagane.',
      };
    }

    return { error: false };
  };

  /**
   * input file size validation test.
   *
   * @returns {boolean}
   */

  const size = (value, type, size) => {
    const validSize = 2097152;

    return size <= validSize
      ? { error: false }
      : {
          error: true,
          msg: 'Zbyt duży rozmiar pliku',
        };
  };

  /**
   * input image file validation test.
   *
   * @returns {boolean}
   */
  const file = (value, type) => {
    const validExt = ['application/pdf', 'image/png', 'image/jpg', 'image/jpeg'];

    return validExt.includes(value)
      ? { error: false }
      : {
          error: true,
          msg: 'Błędny format pliku',
        };
  };

  /**
   * Name/surname validation test
   *
   * @param {String} value
   * @returns {Boolean}
   */
  const name = (value) => {
    return /^[a-zżźćńółęąś\s\-]+$/i.test(value)
      ? { error: false }
      : {
          error: true,
          msg: 'Pole może zawierać tylko litery',
        };
  };

  const email = (value) => {
    if (value === '') return true;

    let polishLetters = /[ąĄćĆęĘłŁńŃóÓśŚźŹżŻ]/;

    if (value.indexOf('@') > -1) {
      let name = value.split('@')[0];

      if (name.length > 64) {
        return {
          error: true,
          msg: 'Nieprawidłowy format.',
        };
      }
    }

    if (value.match(polishLetters) !== null) {
      return {
        error: true,
        msg: 'Nieprawidłowy format.',
      };
    }

    if (!value) {
      return {
        error: true,
        msg: 'Nieprawidłowy format.',
      };
    }

    return /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(
      value,
    )
      ? { error: false }
      : {
          error: true,
          msg: 'Nieprawidłowy format.',
        };
  };

  const emailAgreement = (value) => {
    return true;
  };

  const phoneAgreement = (value) => {
    const isChecked = document.querySelector('[data-agreement-phone]');

    if (value === '' && isChecked.checked)
      return {
        error: true,
        errorFallback: true, // allow to pass the validation after second submit
        msg: 'Enter phone number if you agree to be contacted.',
      };

    return true;
  };

  const agreementEmail = (value) => {
    const field = document.getElementById('email');

    if (!value && field.value !== '')
      return {
        error: true,
        errorFallback: true, // allow to pass the validation after second submit
        msg: 'Select this field if you agree the email contact.',
      };

    return true;
  };

  const agreementPhone = (value) => {
    const field = document.getElementById('phone');

    if (!value && field.value !== '')
      return {
        error: true,
        errorFallback: true, // allow to pass the validation after second submit
        msg: 'Select this field if you agree to phone contact.',
      };

    return true;
  };

  const phone = (value) => {
    if (value === '') return true;

    return /^\+?[(]?[0-9]{1,4}[)]?[-\s\.]?[(]?[0-9]{1,4}[)]?[-\s\.]?[0-9]{1,4}[-\s\.]?[0-9]{1,6}$/im.test(value)
      ? { error: false }
      : {
          error: true,
          msg: 'Nieprawidłowy format.',
        };
  };

  const nip = (nip) => {
    if (!nip) {
      return true;
    }

    nip = nip.replace(/-/g, ''); // nip without dashes

    const reg = /^[0-9]{10}$/;

    if (!reg.test(nip)) {
      return {
        error: true,
        msg: 'Podany numer NIP jest nieprawidłowy',
      };
    }

    const dig = ('' + nip).split('');

    const control =
      (6 * parseInt(dig[0]) +
        5 * parseInt(dig[1]) +
        7 * parseInt(dig[2]) +
        2 * parseInt(dig[3]) +
        3 * parseInt(dig[4]) +
        4 * parseInt(dig[5]) +
        5 * parseInt(dig[6]) +
        6 * parseInt(dig[7]) +
        7 * parseInt(dig[8])) %
      11;

    if (parseInt(dig[9]) === control) {
      return { error: false };
    }

    return {
      error: true,
      msg: 'Podany numer NIP jest nieprawidłowy',
    };
  };

  const minLength = (value, _type, _size, element) => {
    const min = element?.minLength > 0 ? element.minLength : 10;
    return value.trim().length >= min
      ? { error: false }
      : {
          error: true,
          msg: `Minimalna długość to ${min} znaków.`,
        };
  };

  const maxLength = (value, _type, _size, element) => {
    const max = element?.maxLength > 0 ? element.maxLength : Infinity;
    if (max === Infinity) return { error: false };
    return value.trim().length <= max
      ? { error: false }
      : {
          error: true,
          msg: `Maksymalna długość to ${max} znaków.`,
        };
  };

  const postalCode = (value) => {
    return /^[0-9]{2}\-[0-9]{3}$/.test(value)
      ? { error: false }
      : {
          error: true,
          msg: 'Błędny kod pocztowy',
        };
  };

  return {
    init: init,
    returnValidationRule: returnValidationRule,
  };
})();

export { validationRules };
